<script setup lang="ts">
import { isAssignmentReferenceChecklistDraft } from "~/utils/assignment-reference-checklist";
const documentId = inject("documentId") as any;
const documentStore = useSubmittedDocumentStore(documentId);
const { submittedDocument } = storeToRefs(documentStore);
const isReferenceChecklistMode = computed(() =>
  isAssignmentReferenceChecklistDraft(submittedDocument.value)
);
const referenceChecklistStore = useAssignmentReferenceChecklist(documentId)();

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isCompanionMode } = storeToRefs(documentViewModeStore);
const { access: writingToolsAccess } =
  useStudentWritingToolsAccess(documentId);

const thesaurusStore = useDocumentThesaurusStore(documentId);
const { underlinedWords } = storeToRefs(thesaurusStore);

const dressUpsToTag = computed(() => {
  return underlinedWords.value.filter((word) => {
    return (
      Object.keys(word.attributes).some((attribute) => {
        return attribute.toLowerCase().includes("dressup");
      }) != true
    );
  });
});

const tabs = computed(() => {
  const baseTabs: { label: string; key: string }[] = [];

  if (
    isCompanionMode.value == true ||
    writingToolsAccess.value.canUseThesaurus == true
  ) {
    baseTabs.push({
      label: "Thesaurus",
      key: "thesaurus",
    });
  }

  if (
    submittedDocument.value?.documentSubmissionType ==
    DocumentSubmissionType.keyWordOutline
  ) {
    selectedTab.value = baseTabs[0]?.key ?? "checklist";
    return baseTabs;
  }

  if (submittedDocument.value?.assignmentType == AssignmentType.general) {
    selectedTab.value = baseTabs[0]?.key ?? "checklist";
    return baseTabs;
  }

  if (
    submittedDocument.value?.checklistId == undefined &&
    !isReferenceChecklistMode.value
  ) {
    selectedTab.value = baseTabs[0]?.key ?? "checklist";
    return baseTabs;
  }

  return [
    {
      label: "Checklist",
      key: "checklist",
    },
    ...baseTabs,
  ];
});

const selectedTab = ref("checklist");

const isSubmitting = ref(false);
const startGuidedReview = async () => {
  if (isSubmitting.value || submittedDocument.value == undefined) return;

  const previousState = submittedDocument.value.state;
  isSubmitting.value = true;
  submittedDocument.value.state = DocumentState.studentReview;

  try {
    await useDocuments().updateDocumentStateById(
      documentId,
      DocumentState.studentReview
    );
    if (isReferenceChecklistMode.value) {
      referenceChecklistStore.hasContinuedToReview = true;
    }
  } catch (error) {
    submittedDocument.value.state = previousState;
    useBaseToast(
      isReferenceChecklistMode.value
        ? "Review could not be started. Please try again."
        : "Oops! Guided Review could not be started. Please try again.",
      "error"
    );
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  thesaurusStore.initialize();
  thesaurusStore.thesaurusEventEmitter.subscribe((action) => {
    if (action.event == ThesaurusAction.openThesaurus) {
      selectedTab.value = "thesaurus";
    }
  });
});
</script>

<template>
  <div class="h-full flex flex-col min-h-0 p-2 min-w-[500px] max-w-[500px]">
    <BaseTabBar :tabs="tabs" v-model="selectedTab" class="shrink-0" />

    <div class="grow flex flex-col min-h-0 overflow-auto">
      <DocumentStudentEditingReferenceChecklistContainer
        v-if="selectedTab == 'checklist' && isReferenceChecklistMode"
      />
      <DocumentStudentEditingChecklistContainer
        v-if="selectedTab == 'checklist' && !isReferenceChecklistMode"
      />
      <DocumentStudentEditingThesaurusContainer
        v-if="selectedTab == 'thesaurus'"
      />
      <DocumentStudentEditingDressupsContainer
        v-if="selectedTab == 'dress-ups'"
        :dress-ups-to-tag="underlinedWords"
      />
    </div>

    <div class="border-t p-2 shrink-0">
      <BaseButton :show-spinner="isSubmitting" @click="startGuidedReview">
        {{ isReferenceChecklistMode ? "Continue to Review" : "Start Guided Review" }}
      </BaseButton>
    </div>
  </div>
</template>

<style scoped></style>
